/**
 * Estado de la integracion con Random (RND-01, RND-02, ADM-09).
 *
 * Random es la fuente de verdad del stock y de los documentos tributarios. Esta
 * pantalla existe para responder dos preguntas y nada mas: si la sincronizacion
 * esta corriendo, y si hay algun pedido pagado que Random todavia no tomo. Lo
 * segundo va arriba, porque es lo que cuesta plata.
 *
 * **No hay boton para empujar un pedido.** Desde el 15-09-2026 los pedidos no
 * se mandan: Random los consulta. Si uno lleva demasiado tiempo sin que lo
 * tomen, lo unico que se puede hacer desde aca es avisarles — y por eso la
 * tabla muestra desde cuando esta esperando, en vez de una accion que no
 * existe.
 *
 * Los que ya pasaron el plazo (30 minutos, `INTEGRACION_ALERTA_SIN_TOMAR_MINUTOS`)
 * se marcan en rojo: son los que el sitio ya aviso por correo, y los que hay
 * que conversar con Random. Los demas estan esperando su turno normal.
 *
 * Debajo va la unica decision que la sincronizacion del catalogo no toma
 * sola: aceptar el borrado de los productos que Random dejo de informar
 * cuando fueron demasiados (`PanelNoInformados`, RND-01). Aparece solo
 * cuando hay alguno esperando.
 *
 * Mientras el cliente no encienda la API de Random, la sincronizacion de stock
 * viaja apagada. La pantalla lo dice con esas palabras y no como "sin
 * respuesta": son cosas distintas, y confundirlas manda a alguien a buscar una
 * falla que no existe.
 */
import { useState } from 'react';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { LineaTiempo } from '../../componentes/administracion/LineaTiempo';
import { PanelAdmin } from '../../componentes/administracion/PanelAdmin';
import { PanelNoInformados } from '../../componentes/administracion/PanelNoInformados';
import { Tabla } from '../../componentes/administracion/Tabla';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import { obtenerIntegracionRandom, sincronizarConRandom } from '../../servicios/administracion.servicio';
import { fechaHora, numero, pesos } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';

/** Como se rotula el estado de la integracion en la cabecera. */
function estadoDe(habilitada: boolean, conectado: boolean): { clase: string; texto: string } {
  if (!habilitada) return { clase: 'etq etq-neutra', texto: 'Apagada' };
  return conectado
    ? { clase: 'etq etq-ok', texto: 'Conectado' }
    : { clase: 'etq etq-critico', texto: 'Sin respuesta' };
}

export function Random() {
  // Cambia al terminar una sincronizacion manual y obliga a releer el estado.
  const [version, setVersion] = useState(0);
  const [sincronizando, setSincronizando] = useState(false);
  const { estado, datos, error } = usePeticion(
    (senal) => obtenerIntegracionRandom(senal),
    [version],
  );

  async function sincronizar(): Promise<void> {
    setSincronizando(true);
    try {
      await sincronizarConRandom();
    } finally {
      setSincronizando(false);
      setVersion((valor) => valor + 1);
    }
  }

  return (
    <>
      <Seo
        titulo="Integración Random"
        descripcion="Estado de la sincronización de stock y del registro de pedidos."
        ruta={rutas.administracion.random()}
        sinIndexar
      />

      <BarraAdmin
        titulo="Integración Random"
        bajada="Stock, boletas y facturas: Random es la fuente de verdad"
        acciones={
          datos !== null ? (
            <>
              <span className={estadoDe(datos.habilitada, datos.conectado).clase}>
                <span className="punto punto-vivo" />
                {estadoDe(datos.habilitada, datos.conectado).texto}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-contorno"
                onClick={() => {
                  void sincronizar();
                }}
                disabled={sincronizando}
              >
                <Icono nombre="sincro" />
                {sincronizando ? 'Sincronizando…' : 'Sincronizar ahora'}
              </button>
            </>
          ) : undefined
        }
      />

      <div className="admin-cuerpo">
        {estado === 'cargando' ? <Cargando que="la integración" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {datos !== null ? (
          <>
            {!datos.habilitada ? (
              <PanelAdmin
                titulo="La integración todavía no está conectada"
                bajada="Falta que Random habilite su API y entregue las credenciales"
              >
                <p className="parrafo-panel">
                  El conector está construido y apagado con una bandera. Cuando lleguen la URL y
                  la llave de la API, se encienden en la configuración del servidor y esta misma
                  pantalla empieza a mostrar corridas reales. Mientras tanto, el stock que se ve
                  en la tienda es el de la última carga y no se revalida al pagar.
                </p>
              </PanelAdmin>
            ) : null}

            {datos.pedidosSinSincronizar.length > 0 ? (
              <PanelAdmin
                titulo="Pedidos pagados que Random todavía no tomó"
                bajada="Se cobró al cliente y la venta aún no está en el ERP. Random los consulta solo; los que se pasan del plazo llegan también como alerta por correo."
                variante="plano"
                extra={
                  <span className="etq etq-alerta">
                    <Icono nombre="alerta" />
                    {numero(datos.pedidosSinSincronizar.length)} sin tomar
                  </span>
                }
              >
                <Tabla
                  titulo="Pedidos que Random todavía no tomó"
                  encabezados={
                    <>
                      <th scope="col">Pedido</th>
                      <th scope="col">Cliente</th>
                      <th scope="col" className="der">
                        Total
                      </th>
                      <th scope="col">Pagado</th>
                      <th scope="col">Espera</th>
                    </>
                  }
                >
                  {datos.pedidosSinSincronizar.map((pedido) => (
                    <tr key={pedido.id}>
                      <th scope="row">
                        <span className="mono">{pedido.referencia}</span>
                        <span className="s">
                          <time dateTime={pedido.creadoEn}>{fechaHora(pedido.creadoEn)}</time>
                        </span>
                      </th>
                      <td>{pedido.contacto.nombre}</td>
                      <td className="der num">{pesos(pedido.total)}</td>
                      <td>
                        {pedido.pagadoEn !== null ? (
                          <time dateTime={pedido.pagadoEn}>{fechaHora(pedido.pagadoEn)}</time>
                        ) : null}
                      </td>
                      <td>
                        {pedido.estadoSincronizacionRandom === 'sin_tomar' ? (
                          <span className="etq etq-critico">Fuera de plazo</span>
                        ) : (
                          <span className="etq etq-neutra">En plazo</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </Tabla>
              </PanelAdmin>
            ) : null}

            <PanelNoInformados
              cantidad={datos.productosNoInformados}
              onEliminado={() => {
                setVersion((valor) => valor + 1);
              }}
            />

            <div className="panel-doble">
              <PanelAdmin
                titulo="Últimas sincronizaciones del catálogo"
                bajada="Cada corrida trae nombre, precio, stock y clasificación desde Random"
              >
                <LineaTiempo sincronizaciones={datos.historial} />
              </PanelAdmin>

              <PanelAdmin titulo="Próxima corrida">
                <ul className="lista-cifras">
                  <li>
                    <span>Última sincronización</span>
                    <span className="etq etq-neutra">
                      {datos.ultimaSincronizacion !== null
                        ? fechaHora(datos.ultimaSincronizacion.iniciadaEn)
                        : 'Sin datos'}
                    </span>
                  </li>
                  <li>
                    <span>Próxima programada</span>
                    <span className="etq etq-neutra">
                      {datos.proximaSincronizacion !== null
                        ? fechaHora(datos.proximaSincronizacion)
                        : 'Sin programar'}
                    </span>
                  </li>
                  <li>
                    <span>Productos en la última corrida</span>
                    <span className="etq etq-neutra">
                      {datos.ultimaSincronizacion !== null
                        ? numero(datos.ultimaSincronizacion.productosActualizados)
                        : '—'}
                    </span>
                  </li>
                </ul>
              </PanelAdmin>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}
